<script setup>
  import { ref, inject, onMounted, computed } from 'vue'
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import TextDialog from '@/components/TextDialog.vue'
  import EditDialog from '@/components/EditDialog.vue'
  import { Consts } from '@/components/logics/Consts'
  import { marked } from 'marked'
  import { useRouter, RouterLink, RouterView } from 'vue-router'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useUserStore } from '@/stores/userStore'
  import { useDashboardStore } from '@/stores/dashboardStore'

  const router = useRouter()
  const dialogStore = useDialogStore()
  const userStore = useUserStore()
  const dashboardStore = useDashboardStore()

  // routeのnameを取得
  const title = router.currentRoute.value.meta.title
  const consts = Consts()

  const breadcrumbs = [{ name: title }]

  // お知らせ関連の追加
  const announcementText = ref('')
  const isEditingAnnouncement = ref(false)
  const isSavingAnnouncement = ref(false)

  const isNew = date => {
    const today = new Date()
    const postDate = new Date(date)
    const diff = today.getTime() - postDate.getTime()
    return diff < 1000 * 60 * 60 * 24 * 3 // 3 days
  }

  const showPopupPost = ref(false)
  const currentPost = ref([])

  const showPopupTerms = ref(false)
  const termsValue = ref()
  const showPopupPassword = ref(false)
  const showPasswordChange = ref(false)
  const newPassword = ref('')

  const isUser = computed(() => {
    return dashboardStore.dashboardData ? dashboardStore.dashboardData.Manager === false && dashboardStore.dashboardData.Admin === false : false
  })

  const isAppDataExist = computed(() => {
    return dashboardStore.isDashboardDataLoaded
  })

  // ======================================================================
  onMounted(async () => {
    termsValue.value = marked.parse(consts.terms().text)
    await dashboardStore.getDashboardData()

    if (!userStore.currentUser) {
      await userStore.getCurrentUser()
    }

    // お知らせの初期値設定
    announcementText.value = dashboardStore.announcement

    // ダイアログを順番に表示
    try {
      // 1. 利用規約
      if (userStore.isAgreedToTerms(consts.terms().revision)) {
        showPopupTerms.value = true
        await waitForDialogClose(() => showPopupTerms.value)
      }

      // 2. パスワードリセット
      if (userStore.isPasswordResetRequired()) {
        showPopupPassword.value = true
        await waitForDialogClose(() => showPopupPassword.value)
      }

      // 3. システムお知らせ
      if (dashboardStore.newSystemInfos && dashboardStore.newSystemInfos.length > 0) {
        for (const info of dashboardStore.newSystemInfos) {
          const title = info.is_important ? `【重要】${info.title}` : info.title
          await dialogStore.showMessageDialog('システムからのお知らせ', `${title}\n\n${info.text}`)
        }
      }
    } catch (error) {}
  })

  // waitForDialogClose ヘルパー関数を追加
  const waitForDialogClose = checkFn => {
    return new Promise(resolve => {
      const interval = setInterval(() => {
        if (!checkFn()) {
          clearInterval(interval)
          resolve()
        }
      }, 100)
    })
  }

  // パスワード変更
  const changePassword = async () => {
    const message = await userStore.resetPassword(newPassword.value)
    if (message) {
      await dialogStore.showMessageDialog('ディーモ', 'パスワードを変更しました。')
      showPopupPassword.value = false
      newPassword.value = ''
      return
    }
  }

  // 利用規約同意
  const confirmTerms = async () => {
    const message = await userStore.agreeToTerms(consts.terms().revision)
    if (message) {
      await dialogStore.showMessageDialog('ディーモ', '利用規約に同意しました。')
      showPopupTerms.value = false
      return
    }
  }

  // お知らせ編集モード切り替え
  const toggleAnnouncementEdit = () => {
    if (isEditingAnnouncement.value) {
      // キャンセル時は元の値に戻す
      announcementText.value = dashboardStore.announcement
    }
    isEditingAnnouncement.value = !isEditingAnnouncement.value
  }

  // お知らせ保存
  const saveAnnouncement = async () => {
    isSavingAnnouncement.value = true
    const success = await dashboardStore.updateAnnouncement(announcementText.value)

    if (success) {
      await dialogStore.showMessageDialog('ディーモ', 'お知らせを更新しました。')
      isEditingAnnouncement.value = false
    } else {
      await dialogStore.showMessageDialog('エラー', 'お知らせの更新に失敗しました。')
    }
    isSavingAnnouncement.value = false
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <h2>{{ title }}</h2>
      <router-link class="tab-btn" to="/dashboard-graph">業務負荷グラフ</router-link>

      <!-- お知らせ表示エリア -->
      <div class="announcement-area" :class="{ editing: isEditingAnnouncement }">
        <div class="announcement-label">お知らせ：</div>
        <div v-if="!isEditingAnnouncement" class="announcement-display">
          <span v-if="dashboardStore.announcement">{{ dashboardStore.announcement }}</span>
          <span v-else class="no-announcement">お知らせはありません</span>
        </div>
        <input v-if="isEditingAnnouncement" v-model="announcementText" type="text" class="announcement-input" maxlength="255" placeholder="お知らせを入力してください（255文字まで）" />

        <!-- 管理者のみ編集ボタン表示 -->
        <div v-if="userStore.isAdmin()" class="announcement-actions">
          <button v-if="!isEditingAnnouncement" @click="toggleAnnouncementEdit" class="edit-btn">編集</button>
          <template v-else>
            <button @click="saveAnnouncement" class="save-btn" :disabled="isSavingAnnouncement">保存</button>
            <button @click="toggleAnnouncementEdit" class="cancel-btn" :disabled="isSavingAnnouncement">キャンセル</button>
          </template>
        </div>
      </div>
    </div>
    <div class="main-body">
      <div class="dashboard-content">
        <div class="grid-left">
          <div class="grid-area">
            <div class="grid-area-icon">
              <img src="@/assets/img/dashboard_progress_icon.svg" />
            </div>
            <div class="grid-area-header">
              <h3>案件進捗</h3>
              <button type="button" @click="router.push({ path: 'todo', query: { action: 'new' } })">新規登録</button>
            </div>
            <div class="grid-area-content">
              <div class="grid-area-content-inner scroller">
                <table class="todo">
                  <tbody v-if="dashboardStore.isDashboardDataLoaded">
                    <tr v-for="todoType in dashboardStore.todoTypes" :key="todoType" @click="router.push({ path: 'todo', query: { searchText: todoType } })">
                      <td>{{ todoType }}</td>
                      <td>未完了</td>
                      <td>{{ dashboardStore.getTodoSummaryByType(todoType).count }}件</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div class="grid-center">
          <div class="grid-area">
            <div class="grid-area-icon">
              <img src="@/assets/img/dashboard_subsidy_icon.svg" />
            </div>
            <div class="grid-area-header">
              <h3>助成金</h3>
              <button type="button" @click="router.push({ path: 'subsidy', query: { action: 'new' } })">新規登録</button>
            </div>
            <div class="grid-area-content">
              <div class="grid-area-content-inner scroller">
                <table class="subsidy">
                  <thead>
                    <tr>
                      <th>状況</th>
                      <th>全件数</th>
                      <th>期限間近</th>
                      <th>期限超過</th>
                    </tr>
                  </thead>
                  <tbody v-if="dashboardStore.isDashboardDataLoaded">
                    <tr v-for="status in dashboardStore.subsidyStatuses" :key="status" @click="router.push({ path: 'subsidy', query: { status: status } })">
                      <td>
                        <span :class="dashboardStore.colorClass[status]">{{ status || '　' }}</span>
                      </td>
                      <td>{{ dashboardStore.getSubsidySummaryByStatus(status).totalCount }}件</td>
                      <td :class="{ alert: dashboardStore.getSubsidySummaryByStatus(status).urgentCount }">{{ dashboardStore.getSubsidySummaryByStatus(status).urgentCount }}件</td>
                      <td :class="{ alert: dashboardStore.getSubsidySummaryByStatus(status).overdueCount }">{{ dashboardStore.getSubsidySummaryByStatus(status).overdueCount }}件</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div class="grid-right">
          <div class="grid-area">
            <div class="grid-area-icon">
              <img src="@/assets/img/dashboard_request_icon.svg" />
            </div>
            <div class="grid-area-header">
              <h3>電子申請</h3>
              <button type="button" @click="router.push({ path: 'electronicapplication', query: { action: 'new' } })">新規登録</button>
            </div>
            <div class="grid-area-content">
              <div class="grid-area-content-inner scroller">
                <table class="subsidy">
                  <thead>
                    <tr>
                      <th>状況</th>
                      <th>全件数</th>
                      <th>期限間近</th>
                      <th>期限超過</th>
                    </tr>
                  </thead>
                  <tbody v-if="dashboardStore.isDashboardDataLoaded">
                    <tr v-for="status in dashboardStore.electronicApplicationStatuses" :key="status" @click="router.push({ path: 'electronicapplication', query: { status: status } })">
                      <td>
                        <span :class="dashboardStore.colorClass[status]">{{ status || '　' }}</span>
                      </td>
                      <td>{{ dashboardStore.getElectronicApplicationSummaryByStatus(status).totalCount }}件</td>
                      <td :class="{ alert: dashboardStore.getElectronicApplicationSummaryByStatus(status).urgentCount }">{{ dashboardStore.getElectronicApplicationSummaryByStatus(status).urgentCount }}件</td>
                      <td :class="{ alert: dashboardStore.getElectronicApplicationSummaryByStatus(status).overdueCount }">{{ dashboardStore.getElectronicApplicationSummaryByStatus(status).overdueCount }}件</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div class="grid-area">
            <div class="grid-area-icon">
              <img src="@/assets/img/dashboard_36_icon.svg" />
            </div>
            <div class="grid-area-header">
              <h3>36協定</h3>
              <button type="button" @click="router.push({ path: 'article36agreement', query: { action: 'new' } })">新規登録</button>
            </div>
            <div class="grid-area-content">
              <div class="grid-area-content-inner scroller">
                <table class="subsidy">
                  <thead>
                    <tr>
                      <th>状況</th>
                      <th>全件数</th>
                      <th>期限間近</th>
                      <th>期限超過</th>
                    </tr>
                  </thead>
                  <tbody v-if="dashboardStore.isDashboardDataLoaded">
                    <tr v-for="status in dashboardStore.article36AgreementStatuses" :key="status" @click="router.push({ path: 'article36agreement', query: { status: status } })">
                      <td>
                        <span :class="dashboardStore.colorClass[status]">{{ status || '　' }}</span>
                      </td>
                      <td>{{ dashboardStore.getArticle36AgreementSummaryByStatus(status).totalCount }}件</td>
                      <td :class="{ alert: dashboardStore.getArticle36AgreementSummaryByStatus(status).urgentCount }">{{ dashboardStore.getArticle36AgreementSummaryByStatus(status).urgentCount }}件</td>
                      <td :class="{ alert: dashboardStore.getArticle36AgreementSummaryByStatus(status).overdueCount }">{{ dashboardStore.getArticle36AgreementSummaryByStatus(status).overdueCount }}件</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />

  <!-- ダイアログ類は元のまま -->
  <TextDialog v-model:visible="showPopupPost" v-if="currentPost" small :show-cancel-button="false" v-bind:show-comfirm-button="false">
    <template v-slot:top>
      <div class="dialog-post-top">
        <time>{{ currentPost[0].replaceAll('-', '.') }}</time>
        <div class="user">{{ dashboardStore.dashboardData?.Dic[currentPost[1]]?.Name }}</div>
      </div>
    </template>
    <template v-slot:title>{{ currentPost[2] }}</template>
    <template v-slot:content>
      <div class="dialog-post">
        {{ currentPost[3] }}
      </div>
    </template>
  </TextDialog>

  <EditDialog v-model:visible="showPopupPassword" small @save="changePassword" :hide-close-button="true" :show-copy-button="false" :show-delete-button="false" save-button-text="OK" :disabled-save-button="!newPassword">
    <template v-slot:title>パスワード変更</template>
    <template v-slot:content>
      <div class="dialog-edit">
        <dl>
          <dt>新しいパスワード</dt>
          <dd>
            <div class="password">
              <input v-if="showPasswordChange" type="text" v-model="newPassword" />
              <input v-else type="password" v-model="newPassword" />
              <button type="button" class="show-password" @click="showPasswordChange = !showPasswordChange">
                <span class="material-symbols-outlined">{{ showPasswordChange ? 'visibility_off' : 'visibility' }}</span>
              </button>
            </div>
          </dd>
        </dl>
      </div>
    </template>
  </EditDialog>

  <EditDialog :hide-close-button="true" :show-copy-button="false" :show-delete-button="false" v-model:visible="showPopupTerms" @save="confirmTerms" save-button-text="同意する">
    <template v-slot:title>ディーモ for 社労士利用規約</template>
    <template v-slot:content>
      <div class="markdown scroller" v-html="termsValue"></div>
    </template>
  </EditDialog>
</template>

<style lang="less" scoped>
  // main-headerのスタイル拡張
  .main-header {
    .announcement-area {
      display: grid;
      grid-template-columns: auto auto auto;
      justify-content: start;
      &.editing {
        grid-template-columns: auto 1fr auto;
      }
      gap: 10px;
      flex: 1;
      align-items: center;

      .announcement-label {
        font-size: 14px;
        font-weight: bold;
        color: var(--active-color);
        white-space: nowrap;
      }

      .announcement-display {
        // width: 100%;
        font-size: 14px;
        color: #333;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;

        .no-announcement {
          color: #999;
        }
      }

      .announcement-input {
        flex: 1;
        padding: 5px 10px;
        border: 1px solid #898989;
        border-radius: 3px;
        font-size: 14px;
        background-color: #f7f7f7;

        &:focus {
          outline: none;
          border-color: var(--active-color);
        }
      }

      .announcement-actions {
        display: flex;
        gap: 5px;

        button {
          padding: 5px 15px;
          border: none;
          border-radius: 11px;
          font-size: 12px;
          font-weight: bold;
          cursor: pointer;
          transition: opacity 0.3s;
          white-space: nowrap;
          min-width: 54px;

          &:hover:not(:disabled) {
            opacity: 0.7;
          }

          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        }

        .edit-btn,
        .save-btn {
          background-color: var(--sub-color);
          color: #fff;
        }

        .cancel-btn {
          background-color: #e0e0e0;
          color: #666;
        }
      }
    }

    // レスポンシブ対応
    @media (max-width: 960px) {
      flex-wrap: wrap;

      .announcement-area {
        width: 100%;
        max-width: none;
        margin-top: 10px;
        margin-left: 0;
      }
    }
  }
</style>
